/**
 * Beneficiary field requirements — admin-configurable capture levels for the
 * cart's beneficiary editor (BeneficiaryEditorModal).
 *
 * SystemSettings (values: required | recommended | optional):
 *   beneficiary.ssn.requirement           Bill 2026-07-10, BACKLOG-0863 (seed 126)
 *   beneficiary.phone.requirement         Bill 2026-07-10, BACKLOG-0863 (seed 126);
 *                                         set to required by seed 414
 *   beneficiary.dob.requirement           seed 414
 *   beneficiary.relationship.requirement  seed 414
 * Seed 414 citation: Collette Cruse (Retail Supervisor) life-ring report
 * 2026-10-16 ("DOB, phone, relationship should be required"); Bill approved
 * 2026-10-16.
 *
 * 'required' blocks Save; 'recommended' is a visual nudge; 'optional' shows nothing.
 * Pure (no React) so the gates are unit-tested.
 */
import type { BeneficiaryEntry } from '@/components/primitives';
import { toNamedJsonDate } from '@/utils/jxDates';

export type BeneficiaryFieldRequirement = 'required' | 'recommended' | 'optional';

export const BENEFICIARY_SSN_REQUIREMENT_KEY = 'beneficiary.ssn.requirement';
export const BENEFICIARY_PHONE_REQUIREMENT_KEY = 'beneficiary.phone.requirement';
export const BENEFICIARY_DOB_REQUIREMENT_KEY = 'beneficiary.dob.requirement';
export const BENEFICIARY_RELATIONSHIP_REQUIREMENT_KEY = 'beneficiary.relationship.requirement';
export const DEFAULT_BENEFICIARY_SSN_REQUIREMENT: BeneficiaryFieldRequirement = 'required';
export const DEFAULT_BENEFICIARY_PHONE_REQUIREMENT: BeneficiaryFieldRequirement = 'recommended';
// Approved as required (Collette / Bill 2026-10-16); the code default matches the seeded value.
export const DEFAULT_BENEFICIARY_DOB_REQUIREMENT: BeneficiaryFieldRequirement = 'required';
export const DEFAULT_BENEFICIARY_RELATIONSHIP_REQUIREMENT: BeneficiaryFieldRequirement = 'required';

/**
 * Read one beneficiary field-requirement setting defensively from the config
 * bundle. `config.systemSettings` is the /v3/config/system-settings payload,
 * which is an ARRAY of { settingKey, settingValue } rows at runtime (despite
 * its Record<> type) — tolerate that AND a flat record AND legacy {key,value},
 * mirroring existingCustomer.ts readExistingIdExpiringSoonGate.
 */
export function readBeneficiaryFieldRequirement(
  systemSettings: unknown,
  settingKey: string,
  fallback: BeneficiaryFieldRequirement,
): BeneficiaryFieldRequirement {
  const probe = (v: unknown): BeneficiaryFieldRequirement | null => {
    if (typeof v !== 'string') return null;
    const s = v.trim().toLowerCase();
    return s === 'required' || s === 'recommended' || s === 'optional'
      ? (s as BeneficiaryFieldRequirement)
      : null;
  };
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row.settingKey === 'string' ? row.settingKey
        : typeof row.key === 'string' ? row.key : undefined;
      if (k === settingKey) {
        const r = probe(row.settingValue ?? row.value);
        if (r !== null) return r;
      }
    }
  } else if (systemSettings && typeof systemSettings === 'object') {
    const r = probe((systemSettings as Record<string, unknown>)[settingKey]);
    if (r !== null) return r;
  }
  return fallback;
}

type BenefFields = Pick<BeneficiaryEntry, 'cifId' | 'dob' | 'relationship'>;

/** Relationship values that name an organisation rather than a person (no date of birth). */
const ENTITY_RELATIONSHIPS = new Set(['trust', 'charity']);

/** True for a Trust / Charity beneficiary — exempt from the date-of-birth rule. */
export function benefIsEntity(b: BenefFields): boolean {
  return ENTITY_RELATIONSHIPS.has((b.relationship ?? '').trim().toLowerCase());
}

/**
 * Date-of-birth rule: a real date is entered, OR the row is linked to an
 * existing CIF (Core already holds the birth date — the commit path skips the
 * CustAdd for a linked customer, the same reason a linked row satisfies the
 * phone rule; linking also fills the DOB from Core when CustInq returns one),
 * OR the beneficiary is a Trust / Charity.
 */
export function benefDobSatisfied(b: BenefFields): boolean {
  if (benefIsEntity(b)) return true;
  if ((b.cifId ?? '').trim().length > 0) return true;
  return toNamedJsonDate(b.dob ?? '') !== '';
}

/** Relationship rule: one of the editor's relationship choices is picked. */
export function benefRelationshipSatisfied(b: BenefFields): boolean {
  return (b.relationship ?? '').trim().length > 0;
}
